import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Badge,
  Tooltip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Delete as DeleteIcon, HelpOutline as HelpOutlineIcon } from '@mui/icons-material';
import { useProjectById } from '../../hooks/useProjects';
import { useDeleteEpisode, useUpdateEpisodeAccess } from '../../hooks/usePodcasts';
import { useSegments, useUpdateSegment } from '../../hooks/useSegments';
import { AudioPlayer } from '../../components/player/AudioPlayer';
import { SegmentEditorTabs } from '../../components/segments/SegmentEditorTabs';
import { PodcastSegmentChipStrip } from '../../components/segments/PodcastSegmentChipStrip';
import { ProjectPlayerTour } from '../../components/onboarding/ProjectPlayerTour';
import { useProjectPlayerTour } from '../../hooks/useProjectPlayerTour';
import { useOnlineListeningDrain } from '../../hooks/useListeningTime';
import { ListeningTimeWidget } from '../../components/common/ListeningTimeWidget';
import { TranscriptDownloadModal } from '../../components/common/TranscriptDownloadModal';
import type { PodcastEpisode, Segment } from '../../types/api.types';

export const ProjectPlayer = () => {
  const { t } = useTranslation('player');
  const { projectId, podcastId, episodeId } = useParams<{ projectId: string; podcastId?: string; episodeId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isFocusMode = searchParams.get('focus') === 'true';
  const segmentIdParam = searchParams.get('segmentId');

  // Redirect /player/:projectId/:episodeId to always use focus mode
  useEffect(() => {
    if (episodeId && !isFocusMode) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('focus', 'true');
      navigate(`/player/${projectId}/${episodeId}?${newParams.toString()}`, { replace: true });
    }
  }, [episodeId, isFocusMode, navigate, projectId, searchParams]);
  const [selectedEpisode, setSelectedEpisode] = useState<PodcastEpisode | null>(null);
  const [playSegmentHandler, setPlaySegmentHandler] = useState<
    ((start: number, end: number, loop: boolean, segmentId?: number) => void) | null
  >(null);
  const [skipHandler, setSkipHandler] = useState<
    ((seconds: number) => void) | null
  >(null);
  // Explicit segment selection (chip click or panel play button) — cleared on stop/pause
  const [podcastExplicitSegmentId, setPodcastExplicitSegmentId] = useState<number | null>(null);
  const [isPanelPlaying, setIsPanelPlaying] = useState(false);
  const [isPanelLooping, setIsPanelLooping] = useState(false);
  const [podcastCurrentTime, setPodcastCurrentTime] = useState(0);
  const [lastWorkedPodcastSegmentId, setLastWorkedPodcastSegmentId] = useState<number | null>(null);
  const [editingSegment, setEditingSegment] = useState<Segment | null>(null);
  // Times of a just-created segment awaiting selection once the refreshed segment
  // list contains it (the create API does not return the new segment's id).
  const [pendingSegmentSelection, setPendingSegmentSelection] = useState<{
    startTime: number;
    endTime: number;
  } | null>(null);
  // Practice-reading download modal open state.
  const [podcastDownloadOpen, setPodcastDownloadOpen] = useState(false);
  const hasAutoSelectedRef = useRef(false);
  const hasAppliedSegmentParamRef = useRef(false);
  // Transcript work lock — held while the user is working in the transcript
  // editor. Keeps the pinned segment alive across the boundary pause (the
  // player:stopped event normally clears the pin), mirroring how the YouTube
  // player keeps activeSegmentId pinned during edit mode. Ref, not state: it is
  // read inside the mount-once player:stopped listener.
  const transcriptWorkLockRef = useRef(false);
  // Distinguishes plays we initiate (panel play, segment preview) from the user
  // pressing the waveform play button. A user play means free playback: release
  // the pin and boundary so segments advance again (mirrors YouTube's
  // isProgrammaticPlayRef + native-play-button deselection).
  const programmaticPlayRef = useRef(false);

  const { data: project, isLoading, error } = useProjectById(Number(projectId));
  // Derive active segment from live query so transcript/translation updates immediately after auto-gen
  const { data: podcastSegments = [] } = useSegments(Number(projectId), selectedEpisode?.id ?? 0);
  const { mutate: updateSegment, isPending: isUpdatingSegment } = useUpdateSegment();

  // During free playback, track the segment the playhead is currently in;
  // explicit chip/panel-play clicks override until audio stops.
  // This fixes the bug where the panel froze on segment 1 because the strip's
  // proximity callback was blocked once activeSegmentId became non-null.
  const podcastActiveSegmentId = useMemo(() => {
    if (podcastExplicitSegmentId !== null) return podcastExplicitSegmentId;
    return (
      podcastSegments.find(
        (s) => s.startTime <= podcastCurrentTime && podcastCurrentTime < s.endTime,
      )?.id ?? null
    );
  }, [podcastExplicitSegmentId, podcastSegments, podcastCurrentTime]);

  const podcastActiveSegment = podcastSegments.find(s => s.id === podcastActiveSegmentId) ?? null;

  // Whether any segment has a transcript — used to enable/disable vocab generate button.
  const hasTranscript = podcastSegments.some(s => s.transcripts.length > 0);

  // Derive segments that have user-created transcripts for the practice-reading download modal.
  const downloadSegments = useMemo(
    () =>
      podcastSegments
        .filter(s => s.transcripts.some(t => t.source === 'manual'))
        .map(s => ({
          startTime: s.startTime,
          endTime: s.endTime,
          transcription: s.transcripts.find(t => t.source === 'manual')?.transcription ?? '',
        }))
        .sort((a, b) => a.startTime - b.startTime),
    [podcastSegments],
  );

  const { mutate: deleteEpisode } = useDeleteEpisode();
  const { mutate: updateEpisodeAccess } = useUpdateEpisodeAccess();
  const { showTour, completeTour, skipTour, resetTour, wasSkipped } = useProjectPlayerTour();

  // Drain any localStorage-buffered listening time from previous offline periods.
  useOnlineListeningDrain();
  
  // Get all episodes for this project
  const allEpisodes = project?.podcastEpisodes || [];

  // Filter episodes based on URL structure:
  // - If podcastId is present, filter by podcast (came from /player/:projectId/podcast/:podcastId)
  // - If episodeId is present, show all episodes (came from /player/:projectId/:episodeId)
  // - Otherwise show all episodes
  const filteredEpisodes = podcastId
    ? allEpisodes.filter((episode) => episode.podcastId === Number(podcastId))
    : allEpisodes;

  // Reset auto-select flag when project changes
  useEffect(() => {
    hasAutoSelectedRef.current = false;
  }, [projectId]);

  // Clear explicit selection and panel when playback stops
  useEffect(() => {
    const handler = () => {
      setIsPanelPlaying(false);
      // While the user is working the transcript, keep the segment pinned so the
      // editor survives the pause at the segment boundary.
      if (transcriptWorkLockRef.current) return;
      setPodcastExplicitSegmentId(null);
      setIsPanelLooping(false);
    };
    window.addEventListener('player:stopped', handler);
    return () => window.removeEventListener('player:stopped', handler);
  }, []);

  // Restore last-worked segment from localStorage when episode changes
  useEffect(() => {
    if (!selectedEpisode) return;
    const stored = localStorage.getItem(`scribelate-last-worked-podcast-segment-${selectedEpisode.id}`);
    setLastWorkedPodcastSegmentId(stored ? Number(stored) : null);
    transcriptWorkLockRef.current = false;
    setPodcastExplicitSegmentId(null);
    setIsPanelPlaying(false);
    setIsPanelLooping(false);
    setPodcastCurrentTime(0);
    setPendingSegmentSelection(null);
  }, [selectedEpisode?.id]);

  // Persist last-worked segment to localStorage whenever it changes
  useEffect(() => {
    if (!selectedEpisode || lastWorkedPodcastSegmentId === null) return;
    localStorage.setItem(
      `scribelate-last-worked-podcast-segment-${selectedEpisode.id}`,
      String(lastWorkedPodcastSegmentId),
    );
  }, [lastWorkedPodcastSegmentId, selectedEpisode?.id]);

  // Deep-link: if ?segmentId=N is in the URL, activate that segment once segments are loaded
  useEffect(() => {
    if (!segmentIdParam || podcastSegments.length === 0 || hasAppliedSegmentParamRef.current) return;
    const targetId = Number(segmentIdParam);
    const segment = podcastSegments.find((s) => s.id === targetId);
    if (!segment) return;
    setPodcastExplicitSegmentId(segment.id);
    setLastWorkedPodcastSegmentId(segment.id);
    hasAppliedSegmentParamRef.current = true;
  }, [segmentIdParam, podcastSegments]);

  // Auto-select episode from episodeId/podcastId param or on initial load
  useEffect(() => {
    if (!project || allEpisodes.length === 0) return;
    
    // Only auto-select once per project load
    if (!hasAutoSelectedRef.current) {
      // If episodeId is provided in URL, use it directly
      if (episodeId) {
        const episodeByIdMatch = allEpisodes.find(ep => ep.id === Number(episodeId));
        if (episodeByIdMatch) {
          setSelectedEpisode(episodeByIdMatch);
          updateEpisodeAccess(episodeByIdMatch.id);
          hasAutoSelectedRef.current = true;
          return;
        }
      }
      
      // If podcastId is provided, select the first episode from that podcast
      if (podcastId) {
        const episodesByPodcast = allEpisodes.filter(ep => ep.podcastId === Number(podcastId));
        if (episodesByPodcast.length > 0) {
          setSelectedEpisode(episodesByPodcast[0]);
          updateEpisodeAccess(episodesByPodcast[0].id);
          hasAutoSelectedRef.current = true;
          return;
        }
      }
      
      // If no episode selected yet, select the first one (only on initial load)
      if (!selectedEpisode && filteredEpisodes.length > 0) {
        const firstEpisode = filteredEpisodes[0];
        setSelectedEpisode(firstEpisode);
        updateEpisodeAccess(firstEpisode.id);
        hasAutoSelectedRef.current = true;
      }
    }
  }, [project, podcastId, episodeId, allEpisodes, filteredEpisodes, selectedEpisode, updateEpisodeAccess, projectId]);

  const handleSelectEpisode = (episode: PodcastEpisode) => {
    setSelectedEpisode(episode);
    // Update the last accessed timestamp
    updateEpisodeAccess(episode.id);
  };

  const handleRegisterPlaySegment = useCallback((handler: (start: number, end: number, loop: boolean, segmentId?: number) => void) => {
    setPlaySegmentHandler(() => handler);
  }, []);

  const handlePlaySegment = useCallback((start: number, end: number, loop: boolean = false, segmentId?: number) => {
    if (playSegmentHandler) {
      // Play-starting signals issued while paused will fire a wavesurfer 'play'
      // event — flag it so the playback listener doesn't mistake it for the user
      // pressing the waveform play button. Calls made while already playing
      // (e.g. arming a boundary) emit no 'play' event, so no flag then.
      if ((start >= 0 || start === -2) && !isPanelPlaying) {
        programmaticPlayRef.current = true;
      }
      playSegmentHandler(start, end, loop, segmentId);
    }
  }, [playSegmentHandler, isPanelPlaying]);

  // Mirror actual audio playback into the panel play/pause icon — covers playback
  // started outside the panel (waveform play button, previews), matching how the
  // YouTube controls reflect the real player state.
  //
  // A 'play' without a preceding programmatic request means the user pressed the
  // waveform play button: free playback resumes, so release the transcript lock,
  // the pinned segment, and any armed boundary so segments advance with the
  // playhead again (mirrors YouTube's native-play-button deselection). Looping is
  // the exception — the loop pin must survive, as on YouTube.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ isPlaying?: boolean }>).detail;
      if (typeof detail?.isPlaying !== 'boolean') return;
      setIsPanelPlaying(detail.isPlaying);
      if (detail.isPlaying) {
        if (programmaticPlayRef.current) {
          programmaticPlayRef.current = false;
        } else if (!isPanelLooping) {
          transcriptWorkLockRef.current = false;
          setPodcastExplicitSegmentId(null);
          // Signal -4: disarm any stale segment boundary without touching playback.
          handlePlaySegment(-4, 0, false);
        }
      }
    };
    window.addEventListener('player:playback', handler);
    return () => window.removeEventListener('player:playback', handler);
  }, [isPanelLooping, handlePlaySegment]);

  const handleTimeUpdate = useCallback((time: number) => {
    setPodcastCurrentTime(time);
  }, []);

  const handleChipSegmentClick = useCallback((segment: Segment) => {
    setPodcastExplicitSegmentId(segment.id);
    setLastWorkedPodcastSegmentId(segment.id);
    setIsPanelLooping(false);
    setIsPanelPlaying(false);
    // Seek to segment start without auto-playing (mirrors YouTube chip click behaviour).
    // Signal -3 means seek-only: end carries the target time in seconds.
    handlePlaySegment(-3, segment.startTime, false);
  }, [handlePlaySegment]);

  const handleEditSegment = useCallback((seg: Segment) => {
    setEditingSegment(seg);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingSegment(null);
  }, []);

  const handleUpdateSegment = useCallback(
    (startTime: number, endTime: number) => {
      if (!editingSegment || !selectedEpisode) return;
      updateSegment(
        { id: editingSegment.id, projectId: Number(projectId), episodeId: selectedEpisode.id, startTime, endTime },
        {
          onSuccess: () => {
            handlePlaySegment(startTime, startTime + 0.001, false);
            setEditingSegment(null);
          },
        },
      );
    },
    [editingSegment, selectedEpisode, projectId, updateSegment, handlePlaySegment],
  );

  const handlePreviewSegment = useCallback(
    (startTime: number, endTime: number) => {
      handlePlaySegment(startTime, endTime, false);
    },
    [handlePlaySegment],
  );

  const handleRegisterSkip = useCallback((handler: (seconds: number) => void) => {
    setSkipHandler(() => handler);
  }, []);

  const handleSkip = useCallback((seconds: number) => {
    if (skipHandler) {
      skipHandler(seconds);
    }
  }, [skipHandler]);

  const handlePanelPlay = useCallback(() => {
    if (isPanelPlaying) {
      // Pause — stop playback regardless of loop state
      setIsPanelPlaying(false);
      handlePlaySegment(-1, -1, false);
    } else {
      // Play — resolve which segment to use
      const targetSegment =
        podcastActiveSegment ??
        (lastWorkedPodcastSegmentId
          ? (podcastSegments.find(s => s.id === lastWorkedPodcastSegmentId) ?? null)
          : null);

      setIsPanelPlaying(true);
      if (targetSegment) {
        setPodcastExplicitSegmentId(targetSegment.id);
        handlePlaySegment(targetSegment.startTime, targetSegment.endTime, isPanelLooping, targetSegment.id);
      } else {
        handlePlaySegment(-2, -2, false);
      }
    }
  }, [podcastActiveSegment, isPanelPlaying, isPanelLooping, handlePlaySegment, lastWorkedPodcastSegmentId, podcastSegments]);

  // Toggle-only loop (mirrors YouTube): never starts playback. When the panel is
  // already playing, re-issue the segment with the new loop flag so the AudioPlayer
  // boundary ref re-arms without interrupting playback; when stopped, the flag is
  // applied by handlePanelPlay on the next Play press.
  const handlePanelLoop = useCallback(() => {
    const nextLooping = !isPanelLooping;
    // Resolve which segment to loop — active, last-worked, or nothing
    const targetSegment =
      podcastActiveSegment ??
      (lastWorkedPodcastSegmentId
        ? (podcastSegments.find(s => s.id === lastWorkedPodcastSegmentId) ?? null)
        : null);

    if (!targetSegment) return;

    setIsPanelLooping(nextLooping);
    setPodcastExplicitSegmentId(targetSegment.id);
    if (isPanelPlaying) {
      handlePlaySegment(targetSegment.startTime, targetSegment.endTime, nextLooping, targetSegment.id);
    }
  }, [podcastActiveSegment, lastWorkedPodcastSegmentId, podcastSegments, isPanelLooping, isPanelPlaying, handlePlaySegment]);

  const handlePanelSkipBackward = useCallback(() => handleSkip(-5), [handleSkip]);
  const handlePanelSkipForward = useCallback(() => handleSkip(5), [handleSkip]);

  // Segment boundary lock (parity with YouTube's onSegmentWorked): when the user
  // clicks into the transcript editor, pin the segment and — if audio is playing —
  // arm the AudioPlayer boundary so playback stops at the segment's end instead of
  // advancing while the user is working with the transcript.
  const handleTranscriptSegmentWorked = useCallback(() => {
    if (!podcastActiveSegment) return;
    transcriptWorkLockRef.current = true;
    setPodcastExplicitSegmentId(podcastActiveSegment.id);
    setLastWorkedPodcastSegmentId(podcastActiveSegment.id);
    if (isPanelPlaying) {
      // Playhead is inside the segment, so this arms the boundary without seeking
      // (AudioPlayer resumes in place when the current time is within range).
      handlePlaySegment(
        podcastActiveSegment.startTime,
        podcastActiveSegment.endTime,
        isPanelLooping,
        podcastActiveSegment.id,
      );
    }
  }, [podcastActiveSegment, isPanelPlaying, isPanelLooping, handlePlaySegment]);

  // Release the boundary lock when the user leaves edit mode so free-play
  // auto-progression resumes (mirrors YouTube's onExitEditMode).
  const handleTranscriptExitEditMode = useCallback(() => {
    transcriptWorkLockRef.current = false;
    if (!isPanelLooping) {
      setPodcastExplicitSegmentId(null);
      // Signal -4: disarm the segment boundary without touching playback.
      handlePlaySegment(-4, 0, false);
    }
  }, [isPanelLooping, handlePlaySegment]);

  // Stop playback when the active segment is deleted — otherwise the AudioPlayer
  // keeps looping the deleted time range (mirrors YouTube delete behaviour).
  const handleSegmentDeleted = useCallback(
    (segment: Segment) => {
      if (segment.id !== podcastActiveSegmentId) return;
      handlePlaySegment(-1, -1, false);
      setPodcastExplicitSegmentId(null);
      setIsPanelLooping(false);
      setIsPanelPlaying(false);
    },
    [podcastActiveSegmentId, handlePlaySegment],
  );

  // After creating a segment, seek to its start and select it once the refreshed
  // list contains it (mirrors YouTube post-create select + seek, no autoplay).
  const handleSegmentCreated = useCallback(
    (startTime: number, endTime: number) => {
      setPendingSegmentSelection({ startTime, endTime });
      handlePlaySegment(-3, startTime, false);
    },
    [handlePlaySegment],
  );

  useEffect(() => {
    if (!pendingSegmentSelection) return;
    const seg = podcastSegments.find(
      (s) =>
        Math.abs(s.startTime - pendingSegmentSelection.startTime) < 0.01 &&
        Math.abs(s.endTime - pendingSegmentSelection.endTime) < 0.01,
    );
    if (!seg) return;
    setPodcastExplicitSegmentId(seg.id);
    setLastWorkedPodcastSegmentId(seg.id);
    setPendingSegmentSelection(null);
  }, [pendingSegmentSelection, podcastSegments]);

  const handleDeleteEpisode = (episodeId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    console.log('Delete button clicked for episode:', episodeId);
    if (confirm(t('deleteConfirm'))) {
      console.log('Confirmed delete, calling deleteEpisode mutation with projectId:', projectId, 'episodeId:', episodeId);
      deleteEpisode(
        {
          projectId: Number(projectId),
          episodeId,
        },
        {
          onSuccess: () => {
            console.log('Delete successful');
            if (selectedEpisode?.id === episodeId) {
              setSelectedEpisode(null);
            }
          },
        }
      );
    }
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !project) {
    return (
      <Box>
        <Alert severity="error">{t('failedToLoad')}</Alert>
      </Box>
    );
  }

  if (filteredEpisodes.length === 0) {
    return (
      <Box>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(isFocusMode || podcastId ? `/projects/${projectId}` : '/projects')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">
            {project.projectName}
          </Typography>
        </Box>
        <Alert severity="info">
          {podcastId 
            ? t('noPodcastEpisodes')
            : t('noProjectEpisodes')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate(isFocusMode || podcastId ? `/projects/${projectId}` : '/projects')}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ overflow: 'hidden', flex: 1 }}>
          <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            {project.projectName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            {t('learningFrom', { projectLanguage: project.projectLanguage, userLanguage: project.userLanguage })}
          </Typography>
        </Box>
        <ListeningTimeWidget />
        <Tooltip title={t('showTour', 'Show Tour')}>
          <IconButton onClick={resetTour} color="primary">
            <Badge variant="dot" color="secondary" invisible={!wasSkipped}>
              <HelpOutlineIcon />
            </Badge>
          </IconButton>
        </Tooltip>
      </Box>

      {/* Project Player Tour */}
      <ProjectPlayerTour
        open={showTour}
        onComplete={completeTour}
        onSkip={skipTour}
      />

      {/* Audio Player - sticky at top when episode selected */}
      {selectedEpisode && (
        <Box
          sx={{
            position: 'sticky',
            top: { xs: '56px', sm: '64px' },
            zIndex: 100,
            width: '100%',
            overflow: 'hidden',
            borderRadius: '4px 4px 0 0',
            bgcolor: 'background.paper',
          }}
        >
          <AudioPlayer
            key={selectedEpisode.id}
            audioUrl={selectedEpisode.audioUrl}
            episodeName={selectedEpisode.name}
            projectId={Number(projectId)}
            episodeId={selectedEpisode.id}
            projectLanguage={project.projectLanguage}
            onSegmentCreated={handleSegmentCreated}
            onPlaySegmentRequest={handleRegisterPlaySegment}
            onSkipRequest={handleRegisterSkip}
            onTimeUpdate={handleTimeUpdate}
            editingSegment={editingSegment}
            onUpdateSegment={handleUpdateSegment}
            onCancelEdit={handleCancelEdit}
            onPreviewSegment={handlePreviewSegment}
            isUpdatingSegment={isUpdatingSegment}
          />
        </Box>
      )}

      {/* Episodes List — hidden in focus mode (episode cards on project detail navigate here with ?focus=true) */}
      {!isFocusMode && <Card data-tour="episode-list">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('episodes', { count: filteredEpisodes.length })}
          </Typography>

          {filteredEpisodes.length === 0 ? (
            <Alert severity="info">
              {t('noEpisodesYet')}
            </Alert>
          ) : (
            <List>
              {filteredEpisodes.map((episode) => (
                <ListItem
                  key={episode.id}
                  disablePadding
                  sx={{
                    bgcolor:
                      selectedEpisode?.id === episode.id
                        ? 'action.selected'
                        : 'transparent',
                    mb: 1,
                    borderRadius: 1,
                  }}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={(e) => handleDeleteEpisode(episode.id, e)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemButton onClick={() => handleSelectEpisode(episode)}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">{episode.name}</Typography>
                          {episode.isExplicit && (
                            <Chip label={t('explicit')} size="small" color="warning" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            dangerouslySetInnerHTML={{
                              __html: episode.description?.substring(0, 150) + '...' || '',
                            }}
                            sx={{ mb: 0.5 }}
                          />
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(episode.datePublished)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDuration(episode.duration)}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>}

      {/* Segment chip strip + detail panel — connected card below sticky player */}
      {selectedEpisode && (
        <Paper elevation={0} sx={{ borderRadius: '0 0 4px 4px', overflow: 'hidden' }}>
          <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
            <PodcastSegmentChipStrip
              projectId={Number(projectId)}
              episodeId={selectedEpisode.id}
              activeSegmentId={podcastActiveSegmentId}
              currentTime={podcastCurrentTime}
              lastWorkedSegmentId={lastWorkedPodcastSegmentId}
              onSegmentClick={handleChipSegmentClick}
              onLastWorkedChange={setLastWorkedPodcastSegmentId}
              onDownloadTranscripts={downloadSegments.length > 0 ? () => setPodcastDownloadOpen(true) : undefined}
              onEditSegment={handleEditSegment}
              onSegmentDeleted={handleSegmentDeleted}
            />
          </Box>
          <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
            <SegmentEditorTabs
              segment={podcastActiveSegment ?? undefined}
              projectId={Number(projectId)}
              episodeId={selectedEpisode.id}
              projectLanguage={project?.projectLanguage ?? ''}
              userLanguage={project?.userLanguage ?? ''}
              hasTranscript={hasTranscript}
              segments={podcastSegments}
              onSegmentClick={(id) => {
                setPodcastExplicitSegmentId(id);
                setLastWorkedPodcastSegmentId(id);
              }}
              onSegmentWorked={handleTranscriptSegmentWorked}
              onExitEditMode={handleTranscriptExitEditMode}
              isPlaying={isPanelPlaying}
              isLooping={isPanelLooping}
              onPlay={handlePanelPlay}
              onLoop={podcastActiveSegment ? handlePanelLoop : undefined}
              onSkipBackward={handlePanelSkipBackward}
              onSkipForward={handlePanelSkipForward}
            />
          </Box>
        </Paper>
      )}
      <TranscriptDownloadModal
        open={podcastDownloadOpen}
        onClose={() => setPodcastDownloadOpen(false)}
        title={selectedEpisode?.name ?? ''}
        segments={downloadSegments}
        isLoading={false}
      />
    </Box>
  );
};
